/**
 * services/team-directory.js — resolve chat.agent IDs to human names.
 *
 * #159. The CRM's chat row stores the assigned agent as a 24-char hex
 * ObjectId (team_member_id). Our inbox_agents table maps that id back
 * to { email, name } for every agent who has logged into the PWA.
 * The account owner is NOT in inbox_agents — they live in the
 * customers table and the CRM doesn't expose their operator ID, so
 * any unresolvable ObjectId on an owner-session is assumed to be
 * "the account owner" and rendered as such.
 *
 * Exports:
 *   $team           — nanostore with { byId, byEmail, owner, selfEmail }
 *   hydrateTeam()   — fetch /team-directory and populate the store
 *   resolveAgent(x) — sync lookup, returns { id, email, name, label, isYou, found }
 */

import { atom } from 'nanostores';
import { $session } from '../stores/session.js';
import * as api from './api.js';
import { isPermissionDeniedError, markDegraded, markHealthy } from './permission-degraded.js';

// Shape: {
//   byId:      Map<teamMemberId, { id, email, name, role, isOwner, active }>
//   byEmail:   Map<email,         { id, email, name, role, isOwner, active }>
//   roster:    Array<{ id, email, name, role, isOwner, active }>   // owner first
//   owner:     { id, email } | null
//   selfEmail: string | null
// }
//
// #162: `roster` is the CRM-authoritative ordered list used by the
// Transfer picker. Sourced from /crm/team via the proxy's enriched
// /team-directory route. Includes agents who have never logged into
// the PWA, so the primary user can reassign a chat to any team
// member they've added to the CRM.
export const $team = atom({
  byId:      new Map(),
  byEmail:   new Map(),
  roster:    [],
  owner:     null,
  selfEmail: null,
});

let _hydratePromise = null;

/** Fetch the team directory and populate $team. Idempotent within a session. */
export async function hydrateTeam({ force = false } = {}) {
  if (_hydratePromise && !force) return _hydratePromise;
  _hydratePromise = (async () => {
    try {
      const res = await api.fetchTeamDirectory();
      const data = res?.data || res;
      const roster = Array.isArray(data?.roster) ? data.roster : [];
      const agents = Array.isArray(data?.agents) ? data.agents : [];

      // Index the FULL roster (owner + agents) so resolveAgent can match
      // any team_member_id or email regardless of whether the target is
      // the owner or a team member.
      const byId    = new Map();
      const byEmail = new Map();
      for (const a of roster) {
        if (a.id)    byId.set(String(a.id).toLowerCase(),      a);
        if (a.email) byEmail.set(String(a.email).toLowerCase(), a);
      }
      $team.set({
        byId,
        byEmail,
        roster,
        owner:     data?.owner     || null,
        selfEmail: data?.self_email || null,
      });
      markHealthy('team-directory');
    } catch (e) {
      console.warn('[v2 team] hydrate failed:', e.message);
      // #168 — surface a 403 here as a degraded-permissions hint so the
      // agent sees the dashboard banner instead of a silently empty
      // transfer picker. Likely cause: CRM V2 → Live Chat → Live Chat
      // Access OR CRM V2 → Contact → Read missing on the role.
      if (isPermissionDeniedError(e)) {
        markDegraded('team-directory', e.message);
      }
    }
  })();
  return _hydratePromise;
}

// Re-hydrate on session change. Same pattern as channel-colors.js.
$session.listen((s) => {
  _hydratePromise = null;
  if (s?.accountId) hydrateTeam();
});

// Eager hydrate at import so the first Profile/ContactPanel render has data.
hydrateTeam();

/**
 * Given an agent identifier (email, team_member_id ObjectId, or null),
 * return a resolution result. Callers use it to render "Assigned to X"
 * without null-checking.
 *
 * Return shape:
 *   { id, email, name, label, initials, isYou, found }
 *
 *   - `label`    — human-facing text ("Sambit", "hello@odivend.com",
 *                  "Account owner", or "Agent · 69dc2cde" as final fallback)
 *   - `isYou`    — true when the id/email resolves to the logged-in user
 *   - `found`    — true when the id/email matched a real inbox_agents row
 *                  or the owner, false when we fell back to the hex-prefix
 *                  fallback
 */
export function resolveAgent(idOrEmail) {
  const { byId, byEmail, owner, selfEmail } = $team.get();
  if (!idOrEmail) {
    return {
      id: null, email: null, name: null,
      label:    'Unassigned',
      initials: '—',
      isYou:    false,
      found:    false,
    };
  }

  const raw = String(idOrEmail).trim();
  const lower = raw.toLowerCase();
  const isEmail  = raw.includes('@');
  const isObjectId = /^[a-f0-9]{24}$/i.test(raw);

  // 1. Direct match against the agents roster.
  let hit = null;
  if (isEmail)    hit = byEmail.get(lower);
  else if (isObjectId) hit = byId.get(lower);

  if (hit) {
    const email     = hit.email || null;
    const fullName  = hit.name || null;
    // Display rule (intentional): first name when known, full email when not.
    // We DON'T fall back to email.split('@')[0] — "hello@odivend.com" would
    // render as "hello" which reads as a greeting, not a person.
    const firstName = fullName ? fullName.split(/\s+/)[0] : null;
    const display   = firstName || email || 'Agent';
    const isYou     = !!(selfEmail && email && email.toLowerCase() === selfEmail.toLowerCase());
    return {
      id:       hit.id || null,
      email,
      name:     fullName || email || null,
      label:    isYou ? `You${firstName ? ` · ${firstName}` : ''}` : display,
      initials: _initials(fullName || email),
      isYou,
      found:    true,
    };
  }

  // 2. Owner heuristic — an unknown hex ObjectId on an account where
  //    the logged-in user is the owner almost always IS the owner's
  //    own CRM operator id (they assigned themselves via the PWA, the
  //    CRM stored its internal id, and that id isn't exposed through
  //    any of the endpoints we can query). Fall back to "Account owner"
  //    with the owner's email so the agent sees something meaningful.
  if (isObjectId && owner?.email) {
    const isYou = !!(selfEmail && owner.email.toLowerCase() === selfEmail.toLowerCase());
    const label = isYou ? 'You · Account owner' : 'Account owner';
    return {
      id:       raw,
      email:    owner.email,
      name:     'Account owner',
      label,
      initials: _initials(owner.email),
      isYou,
      found:    false,  // heuristic, not a confirmed match
    };
  }

  // 3. Plain email with no directory hit — render the full email instead
  //    of slicing the local part (see the rationale in case 1).
  if (isEmail) {
    const isYou = !!(selfEmail && lower === selfEmail.toLowerCase());
    return {
      id:       null,
      email:    raw,
      name:     raw,
      label:    isYou ? `You · ${raw}` : raw,
      initials: _initials(raw),
      isYou,
      found:    false,
    };
  }

  // 4. Last-resort hex id fallback. Render a short prefix so the id is
  //    identifiable without dumping the full 24-char string into the UI.
  return {
    id:       raw,
    email:    null,
    name:     null,
    label:    `Agent · ${raw.slice(0, 8)}`,
    initials: raw.slice(0, 2).toUpperCase(),
    isYou:    false,
    found:    false,
  };
}

function _initials(source) {
  const s = String(source || '').trim();
  if (!s) return '—';
  const parts = s.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}
